import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

/**
 * 5-language i18n setup.
 *
 * Supported (per super admin decision):
 *   - en (English)       LTR — default for admin UI
 *   - ar (Arabic)        RTL — primary for admin team + many external users
 *   - ur (Urdu)          RTL — cleaner / pantry worker languages
 *   - hi (Hindi)         LTR — cleaner / pantry worker languages
 *   - fil (Filipino)     LTR — cleaner / pantry worker languages
 *
 * Admin UI defaults to AR + EN only; the cleaner/external mobile UI exposes
 * all 5. Language packs are lazy-loaded from /locales/{lng}/{ns}.json to keep
 * the initial bundle lean.
 *
 * RTL switching:
 *   When language changes, we flip document.dir = 'rtl' for ar/ur, 'ltr' else.
 *   Tailwind 4 logical-property utilities handle most layout flipping
 *   automatically (start/end instead of left/right).
 */

export const LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'ur', label: 'اردو', dir: 'rtl' },
  { code: 'hi', label: 'हिन्दी', dir: 'ltr' },
  { code: 'fil', label: 'Filipino', dir: 'ltr' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

function applyDir(lng: string) {
  const meta = LANGUAGES.find((l) => l.code === lng);
  document.documentElement.dir = meta?.dir ?? 'ltr';
  document.documentElement.lang = lng;
}

export async function initI18n() {
  await i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: 'en',
      supportedLngs: LANGUAGES.map((l) => l.code),
      ns: ['common', 'cleanops', 'pantry', 'platform', 'agent'],
      defaultNS: 'common',
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
      },
      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'admin-hub.lang',
      },
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });

  applyDir(i18n.language);
  i18n.on('languageChanged', applyDir);

  return i18n;
}

export default i18n;
